import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import DeleteButton from 'Components/DeleteButton'
import DrawerRight from 'Components/DrawerRight'
import ListActions from 'Components/ListActions'
import { WithLoader } from 'Components/WithLoader'
import ZoneDetails from 'Services/ControlDataManagement/Features/Zone/Components/ZoneDetails'
import ZoneList from 'Services/ControlDataManagement/Features/Zone/Components/ZoneList'
import { useZonesWithParams } from 'Services/ControlDataManagement/Features/Zone/Hooks'
import { ZoneTabTypes } from 'Services/ControlDataManagement/Features/Zone/types'
import {
  useDeleteSingleZoneMutation,
  useGetSingleZoneQuery
} from 'Services/ControlDataManagement/Features/Zone/zoneApi'
import {
  CONTROL_DATA_MANAGEMENT_ROOT_URL,
  ROWS_PER_PAGE_DEFAULT,
  SORT_ORDER_ASC,
  SORT_ORDER_DESC
} from 'Util/constants'
import { useConfirm } from 'Util/hooks/useConfirm'
import SnackbarUtils from 'Util/Snackbar'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Divider, Tab, Typography } from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

export default function ZoneListPage() {
  const baseUrl = `${CONTROL_DATA_MANAGEMENT_ROOT_URL}/zones`
  const navigate = useNavigate()
  const { confirm } = useConfirm()

  const {
    zones,
    isFetching: isZonesFetching,
    totalResults,
    params,
    setParams,
    selectedZoneId,
    setSelectedZoneId
  } = useZonesWithParams()
  const [isDrawerOpen, setIsDrawerOpen] = useState(!!selectedZoneId)

  const { data: singleZone, isFetching: isSingleZoneFetching } = useGetSingleZoneQuery(
    selectedZoneId ?? '',
    {
      skip: !selectedZoneId
    }
  )

  const [deleteSingleZone, { isLoading: isDeleteLoading }] = useDeleteSingleZoneMutation()

  const handleChangePage = (_event: unknown, newPage: number) => {
    setParams(prevState => ({
      ...prevState,
      start_index: (newPage * ROWS_PER_PAGE_DEFAULT).toString()
    }))
  }

  function handleRequestSort(_event: React.MouseEvent<unknown, MouseEvent>, property: string) {
    const isAsc = params.sort_order === SORT_ORDER_ASC
    setParams(prevState => ({
      ...prevState,
      sort_by: property,
      sort_order: `${isAsc ? SORT_ORDER_DESC : SORT_ORDER_ASC}`
    }))
  }

  const breadcrumbs = [
    {
      title: t`Home`,
      link: CONTROL_DATA_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Zones`
    }
  ]

  const handleRowClick = (id: string) => {
    setSelectedZoneId(id)
    setIsDrawerOpen(true)
  }

  const handleDrawerClose = () => {
    setIsDrawerOpen(false)
    setSelectedZoneId('')
  }

  const handleDelete = () => {
    if (!selectedZoneId) return

    deleteSingleZone(selectedZoneId)
      .unwrap()
      .then(() => {
        handleDrawerClose()
        SnackbarUtils.success(t`Zone successfully deleted.`)
        handleDrawerClose()
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error deleting the zone.`)
      })
  }

  const isLoading = isSingleZoneFetching ?? isDeleteLoading

  return (
    <Box sx={{ width: '100%' }}>
      <Breadcrumbs items={breadcrumbs} />
      <Box sx={{ mb: 2 }}>
        <Typography variant="h1" sx={{ mb: 2 }}>{t`Zones`}</Typography>
        <Box mt={4}>
          <ListActions>
            <CustomButton
              onClick={() => navigate(`${baseUrl}/create/`)}
              size="small"
              startIcon={<AddIcon />}
            >
              {t`Create new zone`}
            </CustomButton>
          </ListActions>
          <Box pt={1}>
            <ZoneList
              rows={zones}
              isLoading={isZonesFetching}
              onRowClick={handleRowClick}
              selectedZoneId={selectedZoneId}
              params={params}
              handleRequestSort={handleRequestSort}
              handleChangePage={handleChangePage}
              count={totalResults}
              page={
                params.start_index ? Number.parseInt(params.start_index) / ROWS_PER_PAGE_DEFAULT : 0
              }
            />
          </Box>
          <DrawerRight
            open={isDrawerOpen}
            onClose={handleDrawerClose}
            topBar={
              <Box>
                <DeleteButton
                  sx={{ mr: 1 }}
                  size="small"
                  onClick={() =>
                    confirm({
                      title: t`Are you sure?`,
                      message: t`Are you sure you want to delete the zone?`,
                      handleConfirm: () => {
                        handleDelete()
                      }
                    })
                  }
                >{t`Delete`}</DeleteButton>
                <CustomButton
                  onClick={() => navigate(`${baseUrl}/${selectedZoneId}/edit`)}
                  size="small"
                  startIcon={<EditIcon />}
                >{t`Edit`}</CustomButton>
              </Box>
            }
          >
            <WithLoader loading={isLoading}>
              {singleZone && (
                <TabContext value={ZoneTabTypes.DETAILS}>
                  <TabList aria-label={t`Details`}>
                    <Tab label={t`Details`} value={ZoneTabTypes.DETAILS} />
                  </TabList>
                  <Divider />
                  <TabPanel sx={{ p: 0 }} value={ZoneTabTypes.DETAILS}>
                    <ZoneDetails zone={singleZone} />
                  </TabPanel>
                </TabContext>
              )}
            </WithLoader>
          </DrawerRight>
        </Box>
      </Box>
    </Box>
  )
}
